<script setup>
/**
 * ConfirmModal — Fenêtre modale de confirmation réutilisable.
 * Remplace les `window.confirm()` par une UI cohérente (titre, message,
 * icône colorée selon la gravité, bouton de confirmation avec spinner).
 *
 * Usage :
 *   <ConfirmModal v-model:visible="open" severity="danger"
 *     title="Archiver ?" message="…" confirm-label="Archiver"
 *     :loading="busy" @confirm="doIt" />
 */
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'

const props = defineProps({
  visible: { type: Boolean, required: true },
  title: { type: String, default: 'Confirmer' },
  message: { type: String, default: 'Confirmez-vous cette action ?' },
  confirmLabel: { type: String, default: 'Confirmer' },
  cancelLabel: { type: String, default: 'Annuler' },
  // 'danger' | 'warning' | 'info'
  severity: { type: String, default: 'danger' },
  icon: { type: String, default: '' },
  loading: { type: Boolean, default: false },
})
const emit = defineEmits(['update:visible', 'confirm', 'cancel'])

const open = computed({
  get: () => props.visible,
  set: (v) => emit('update:visible', v),
})

const META = {
  danger: { icon: 'pi-exclamation-triangle', btn: 'danger' },
  warning: { icon: 'pi-exclamation-circle', btn: 'warn' },
  info: { icon: 'pi-info-circle', btn: 'primary' },
}
const meta = computed(() => META[props.severity] || META.info)

function onConfirm() { emit('confirm') }
function onCancel() { emit('cancel'); open.value = false }
</script>

<template>
  <Dialog
    v-model:visible="open"
    modal
    :draggable="false"
    :dismissable-mask="!loading"
    :closable="!loading"
    :style="{ width: 'min(440px, 94vw)' }"
    :show-header="false"
    content-class="cm-content"
  >
    <div class="cm">
      <div class="cm-icon" :class="`cm-icon--${severity}`">
        <i class="pi" :class="icon || meta.icon"></i>
      </div>
      <h3 class="cm-title">{{ title }}</h3>
      <p class="cm-message">{{ message }}</p>
      <div class="cm-actions">
        <Button :label="cancelLabel" severity="secondary" text :disabled="loading" @click="onCancel" />
        <Button
          :label="loading ? 'Traitement…' : confirmLabel"
          :severity="meta.btn"
          :loading="loading"
          @click="onConfirm"
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.cm { display: flex; flex-direction: column; align-items: center; text-align: center; padding: var(--space-5) var(--space-4) var(--space-4); gap: var(--space-2); }
.cm-icon {
  width: 56px; height: 56px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 26px; margin-bottom: var(--space-1);
}
.cm-icon--danger { background: var(--danger-100, #fee2e2); color: var(--danger-600, #dc2626); }
.cm-icon--warning { background: var(--warning-100, #fef3c7); color: var(--warning-700, #b45309); }
.cm-icon--info { background: var(--primary-100); color: var(--primary-700); }
.cm-title { margin: 0; font-size: var(--text-lg); font-weight: var(--font-bold); color: var(--text-primary); }
.cm-message { margin: 0; font-size: var(--text-sm); color: var(--text-secondary); line-height: 1.5; white-space: pre-line; }
.cm-actions { display: flex; gap: var(--space-2); justify-content: center; margin-top: var(--space-3); width: 100%; }
.cm-actions :deep(.p-button) { min-width: 120px; justify-content: center; }
</style>
